import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  CheckCircle, 
  Calendar, 
  Package, 
  Heart, 
  Home, 
  Eye, 
  MapPin,
  Truck,
  Clock,
  GitCommit,
  ChevronDown,
  ChevronUp,
  Activity,
  XCircle,
  Globe,
  Terminal,
  Webhook,
  User,
  Target,
  AlertTriangle,
  Code,
  Send,
  MousePointer,
  Download
} from 'lucide-react'
import ApiMonitor from '../components/ApiMonitor'
import ThemeToggle from '../components/ThemeToggle'
import { useApiLogs } from '../contexts/ApiLogsContext'

interface LocationState {
  orderNumber?: string
  customerInfo?: {
    name: string
    email: string
    phone: string
  }
  orderTotal?: number
  paymentMethod?: string
  billingAddress?: {
    street: string
    city: string
    state: string
    zipCode: string
    country: string
  }
  cartItems?: Array<{
    id: string
    name: string
    price: number
    quantity: number
  }>
  sessionId?: string
  pubUserId?: string
  apiKey?: string
}

interface ApiLog {
  request: any
  response: any
  loading: boolean
  error: string | null
}

const FinalOrderConfirmationPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  console.log('Location state in FinalOrderConfirmationPage:', location.state)
  const state = location.state ? (location.state as LocationState) : null
  const { addApiLog, updateApiLog, apiLogs, selectionHistory } = useApiLogs()
  
  // API Monitor state
  const [showApiPanel, setShowApiPanel] = useState(false)
  const [sidebarWidth, setSidebarWidth] = useState(480)
  const [sessionDetailsLog, setSessionDetailsLog] = useState<ApiLog | null>(null)
  
  // Journey Timeline state
  const [showJourney, setShowJourney] = useState(false)
  const [expandedJourneyItems, setExpandedJourneyItems] = useState<Set<string>>(new Set())

  // Combine API logs and selection history into unified timeline
  const getUnifiedTimeline = () => {
    const timeline: any[] = []
    
    // Add API logs
    apiLogs.forEach(log => {
      timeline.push({
        ...log,
        type: 'api',
        timestamp: log.timestamp
      })
    })
    
    // Add selection history
    selectionHistory.forEach(entry => {
      timeline.push({
        ...entry,
        type: 'selection',
        timestamp: entry.timestamp
      })
    })
    
    // Sort by timestamp (oldest first for chronological order)
    return timeline.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
  }

  // Toggle journey item expansion
  const toggleJourneyItem = (itemId: string) => {
    setExpandedJourneyItems(prev => {
      const newSet = new Set(prev)
      if (newSet.has(itemId)) {
        newSet.delete(itemId)
      } else {
        newSet.add(itemId)
      }
      return newSet
    })
  }
  
  // API configuration (use values from state or fallback to demo values)
  const apiKey = state?.apiKey || 'demo-api-key'
  const pubUserId = state?.pubUserId || 'demo-pub-user-id'
  const sessionId = state?.sessionId || sessionStorage.getItem('usp_session_id') || 'demo-session-id'
  
  // Store session ID in sessionStorage for persistence across page refreshes
  useEffect(() => {
    if (state?.sessionId) {
      sessionStorage.setItem('usp_session_id', state.sessionId);
      console.log('💾 Session ID stored in final order page:', state.sessionId);
    }
  }, [state?.sessionId]);
  
  // Default values if no state is passed
  const customerInfo = state?.customerInfo || {
    name: 'Sarah Johnson',
    email: 'sarah.johnson@email.com',
    phone: '+1 (555) 123-4567'
  }
  const orderTotal = state?.orderTotal || 155.95
  const billingAddress = state?.billingAddress || {
    street: '123 Flower Street',
    city: 'New York',
    state: 'NY',
    zipCode: '10001',
    country: 'United States'
  }
  const cartItems = state?.cartItems || [
    {
      id: '1',
      name: 'Red Rose Bouquet',
      price: 45.99,
      quantity: 1
    },
    {
      id: '2',
      name: 'Spring Mixed Bouquet',
      price: 32.99,
      quantity: 2
    }
  ]

  const [deliveryDate] = useState(() => {
    const date = new Date()
    date.setDate(date.getDate() + 2) // 2 days from now
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })
  })

  const [estimatedDeliveryTime] = useState(() => {
    return "2:00 PM - 4:00 PM"
  })

  // Get session details (real API implementation)
  const getSessionDetails = async () => {
    const url = `https://api.adspostx.com/sdk/v4/usp/session/${sessionId}.json?pub_user_id=${pubUserId}`
    const headers = { 
      'Authorization': `Bearer ${apiKey}`,
      'Content-Type': 'application/json'
    }

    // Create initial log entry
    const requestData = {
      url,
      method: 'GET',
      headers,
      timestamp: new Date().toISOString()
    }

    // Add to shared API logs
    const logId = addApiLog({
      page: 'final-confirmation',
      action: 'GET /sdk/v4/usp/session/{sessionId}.json',
      request: requestData,
      response: null,
      loading: true,
      error: null
    })

    // Set loading state for local component
    setSessionDetailsLog({
      request: requestData,
      response: null,
      loading: true,
      error: null
    })
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers
      })
      
      let responseData
      let errorMessage = null
      
      try {
        responseData = await response.json()
      } catch (parseError) {
        responseData = { error: 'Failed to parse response as JSON' }
        errorMessage = `HTTP ${response.status}: Invalid JSON response`
      }
      
      // If response is not ok, treat as an error
      if (!response.ok) {
        const httpError = `HTTP ${response.status}: ${response.statusText}`
        errorMessage = responseData.message || responseData.error || httpError
        
        const responseObj = {
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
          data: responseData,
          timestamp: new Date().toISOString()
        }

        // Update shared API logs
        updateApiLog(logId, {
          response: responseObj,
          loading: false,
          error: errorMessage
        })

        setSessionDetailsLog({
          request: {
            url,
            method: 'GET',
            headers,
            timestamp: new Date().toISOString()
          },
          response: responseObj,
          loading: false,
          error: errorMessage
        })
        return
      }
      
      // Update with successful response
      const successResponseObj = {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        data: responseData,
        timestamp: new Date().toISOString()
      }

      // Update shared API logs
      updateApiLog(logId, {
        response: successResponseObj,
        loading: false,
        error: null
      })

      setSessionDetailsLog({
        request: {
          url,
          method: 'GET',
          headers,
          timestamp: new Date().toISOString()
        },
        response: successResponseObj,
        loading: false,
        error: null
      })
      
    } catch (error) {
      // Handle network errors and other exceptions
      let errorMessage = 'Network error occurred'
      
      if (error instanceof TypeError && error.message.includes('fetch')) {
        errorMessage = 'Network error: Unable to connect to API server'
      } else if (error instanceof Error) {
        errorMessage = `Network error: ${error.message}`
      }

      // Update shared API logs
      updateApiLog(logId, {
        response: null,
        loading: false,
        error: errorMessage
      })

      setSessionDetailsLog({
        request: {
          url,
          method: 'GET',
          headers,
          timestamp: new Date().toISOString()
        },
        response: null,
        loading: false,
        error: errorMessage
      })
    }
  }

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.15
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  }

  const checkmarkVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: {
      scale: 1,
      rotate: 0,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 15,
        delay: 0.3
      }
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-rose-100 dark:from-gray-900 dark:to-gray-800 relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-32 h-32 bg-pink-500 rounded-full blur-3xl"></div>
        <div className="absolute top-40 right-20 w-48 h-48 bg-purple-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-1/3 w-40 h-40 bg-blue-500 rounded-full blur-3xl"></div>
      </div>

      {/* Header */}
      <header className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-lg border-b border-pink-200 dark:border-gray-700 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-r from-[#FE4215] to-[#FF6B47] rounded-xl flex items-center justify-center shadow-lg">
                <Heart className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                  Bloom & Blossom
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">Order Confirmation</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="hidden lg:flex items-center space-x-4 text-sm">
                <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
                  <Clock className="w-4 h-4" />
                  <span className="font-medium">Session ID:</span>
                  <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs">
                    {sessionId}
                  </code>
                </div>
                <div className="w-px h-4 bg-gray-300 dark:bg-gray-600"></div>
                <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
                  <span className="font-medium">User ID:</span>
                  <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs">
                    {pubUserId}
                  </code>
                </div>
              </div>
              <button
                onClick={() => setShowApiPanel(!showApiPanel)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  showApiPanel
                    ? 'bg-pink-100 text-pink-700 dark:bg-pink-900/20 dark:text-pink-400'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <Eye className="w-4 h-4" />
                <span>API Monitor</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${showApiPanel ? `pr-[${sidebarWidth}px]` : ''}`}>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-8"
          >
            {/* Success Header */}
            <motion.div
              variants={itemVariants}
              className="text-center"
            >
              <motion.div
                variants={checkmarkVariants}
                className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-r from-pink-400 to-rose-500 rounded-full mb-6 shadow-2xl"
              >
                <CheckCircle className="w-14 h-14 text-white" />
              </motion.div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-pink-600 to-rose-600 dark:from-pink-400 dark:to-rose-400 bg-clip-text text-transparent mb-3">
                Payment Successful!
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-400 mb-4">
                Your order has been confirmed and is being processed
              </p>
              <div className="bg-gradient-to-r from-pink-50 to-rose-50 dark:from-pink-900/20 dark:to-rose-900/20 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-pink-200 dark:border-pink-700 inline-block">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-pink-500 to-rose-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <Heart className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-pink-900 dark:text-pink-100">
                    <span className="font-semibold text-base">Check Your Email!</span>
                    <span className="text-pink-700 dark:text-pink-300 ml-2">
                      We've sent you the amazing perks you selected with your order receipt. Enjoy them!
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Order Summary and Delivery Details - Same Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Order Summary */}
              <motion.div
                variants={itemVariants}
                className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
              >
                <div className="bg-gradient-to-r from-pink-50 to-rose-50 dark:from-pink-900/20 dark:to-rose-900/20 px-6 py-4 border-b border-pink-100 dark:border-pink-800">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
                    <Package className="w-5 h-5 mr-2 text-pink-600 dark:text-pink-400" />
                    Order Summary
                  </h2>
                </div>
                
                <div className="p-6">
                  <div className="space-y-4">
                    {cartItems.map((item) => (
                      <div key={item.id} className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-600 last:border-b-0">
                        <div className="flex items-center space-x-3">
                          <div className="w-16 h-16 rounded-lg overflow-hidden shadow-md">
                            <img 
                              src={item.name === 'Red Rose Bouquet' 
                                ? 'https://images.unsplash.com/photo-1518895949257-7621c3c786d7?w=200&h=200&fit=crop&crop=center' 
                                : 'https://images.unsplash.com/photo-1563241527-3004b7be0ffd?w=200&h=200&fit=crop&crop=center'
                              }
                              alt={item.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">{item.name}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Qty: {item.quantity}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-gray-900 dark:text-white">
                            ${(item.price * item.quantity).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
                    <div className="flex justify-between">
                      <span className="text-lg font-semibold text-gray-900 dark:text-white">Total</span>
                      <span className="text-xl font-bold text-green-600 dark:text-green-400">
                        ${orderTotal.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Delivery Information */}
              <motion.div
                variants={itemVariants}
                className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
              >
                <div className="bg-gradient-to-r from-rose-50 to-pink-50 dark:from-rose-900/20 dark:to-pink-900/20 px-6 py-4 border-b border-rose-100 dark:border-rose-800">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
                    <Truck className="w-5 h-5 mr-2 text-rose-600 dark:text-rose-400" />
                    Delivery Details
                  </h2>
                </div>
                
                <div className="p-6">
                  <div className="space-y-6">
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Delivery Address</p>
                      <div className="flex items-start space-x-2">
                        <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div className="text-gray-900 dark:text-white">
                          <p>{billingAddress.street}</p>
                          <p>{billingAddress.city}, {billingAddress.state} {billingAddress.zipCode}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Expected Delivery</p>
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <p className="text-gray-900 dark:text-white font-medium">{deliveryDate}</p>
                      </div>
                      <div className="flex items-center space-x-2 mt-1">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <p className="text-gray-900 dark:text-white">{estimatedDeliveryTime}</p>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Customer Information</p>
                      <div className="text-gray-900 dark:text-white space-y-1">
                        <p className="font-medium">{customerInfo.name}</p>
                        <p className="text-sm">{customerInfo.email}</p>
                        <p className="text-sm">{customerInfo.phone}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Journey Timeline Section - Better positioned for visibility */}
            <motion.div
              variants={itemVariants}
              className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-pink-50 to-rose-50 dark:from-pink-900/20 dark:to-rose-900/20 px-6 py-4 border-b border-pink-100 dark:border-pink-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-pink-500 to-rose-600 rounded-xl flex items-center justify-center shadow-lg">
                      <Activity className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        Demo Journey Timeline
                      </h2>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Track all API calls made during your demo session
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowJourney(!showJourney)}
                    className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-pink-600 to-rose-700 hover:from-pink-700 hover:to-rose-800 text-white rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    <GitCommit className="w-4 h-4" />
                    <span>{showJourney ? 'Hide Journey' : 'View Journey'}</span>
                    {showJourney ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              {showJourney && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.5 }}
                  className="p-6"
                >
                  {(() => {
                    const timeline = getUnifiedTimeline()
                    return timeline.length > 0 ? (
                      <div className="space-y-6">
                        {timeline.map((item, index) => {
                          const isExpanded = expandedJourneyItems.has(item.id)
                          const isLastItem = index === timeline.length - 1
                          
                          return (
                            <div key={item.id} className="relative">
                              {/* Timeline Line */}
                              {!isLastItem && (
                                <div className="absolute left-6 top-12 w-0.5 h-full bg-gradient-to-b from-purple-300 to-indigo-300 dark:from-purple-700 dark:to-indigo-700"></div>
                              )}
                              
                              {/* Timeline Item */}
                              <div className="relative flex items-start space-x-4">
                                {/* Timeline Dot */}
                                <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                                  item.type === 'api' 
                                    ? 'bg-gradient-to-r from-pink-500 to-rose-600 text-white' 
                                    : 'bg-gradient-to-r from-[#FE4215] to-[#FF6B47] text-white'
                                }`}>
                                  {item.type === 'api' ? (
                                    <Send className="w-5 h-5" />
                                  ) : (
                                    <MousePointer className="w-5 h-5" />
                                  )}
                                </div>
                                
                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                  <div 
                                    className={`bg-white dark:bg-gray-800 rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                                      isExpanded 
                                        ? 'border-purple-300 dark:border-purple-600 shadow-lg' 
                                        : 'border-gray-200 dark:border-gray-700 hover:border-purple-200 dark:hover:border-purple-700 shadow-sm hover:shadow-md'
                                    }`}
                                    onClick={() => toggleJourneyItem(item.id)}
                                  >
                                    <div className="p-4">
                                      <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                          <h4 className="font-semibold text-gray-900 dark:text-white">
                                            {item.type === 'api' ? (
                                              item.action === 'POST /native/v2/offers.json' ? 'Fetched available offers from MomentScience API' :
                                              item.action === 'POST /sdk/v4/usp/{sessionId}/{campaignId}/select.json' ? 'Selected an offer' :
                                              item.action === 'POST /sdk/v4/usp/{sessionId}/{campaignId}/unselect.json' ? 'Unselected an offer' :
                                              item.action === 'PUT /sdk/v4/usp/session/{sessionId}.json (bulk select)' ? 'Selected all offers' :
                                              item.action === 'GET /sdk/v4/usp/session/{sessionId}.json' ? 'Retrieved session details for order confirmation' :
                                              item.action
                                            ) : (
                                              item.action === 'select' ? `Selected "${item.perkTitle}"` :
                                              item.action === 'unselect' ? `Unselected "${item.perkTitle}"` :
                                              item.action === 'bulk_select' ? `Selected all ${item.totalPerks} offers` :
                                              item.action
                                            )}
                                          </h4>
                                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                            {new Date(item.timestamp).toLocaleString()}
                                          </p>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                            item.error 
                                              ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                                              : 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                                          }`}>
                                            {item.error ? 'Failed' : 'Success'}
                                          </span>
                                          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                                            isExpanded ? 'rotate-180' : ''
                                          }`} />
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {isExpanded && (
                                      <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        transition={{ duration: 0.3 }}
                                        className="border-t border-gray-200 dark:border-gray-700 p-4"
                                      >
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                          {/* Request Details */}
                                          {item.type === 'api' && item.request && (
                                            <div>
                                              <h5 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center">
                                                <Send className="w-4 h-4 mr-2 text-blue-500" />
                                                Request Details
                                              </h5>
                                              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 text-sm">
                                                <div className="space-y-2">
                                                  <div>
                                                    <span className="font-medium text-gray-700 dark:text-gray-300">Method:</span>
                                                    <span className="ml-2 text-gray-600 dark:text-gray-400">{item.request.method}</span>
                                                  </div>
                                                  <div>
                                                    <span className="font-medium text-gray-700 dark:text-gray-300">URL:</span>
                                                    <span className="ml-2 text-gray-600 dark:text-gray-400 break-all">{item.request.url}</span>
                                                  </div>
                                                  {item.request.body && (
                                                    <div>
                                                      <span className="font-medium text-gray-700 dark:text-gray-300">Body:</span>
                                                      <pre className="mt-1 text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 p-2 rounded overflow-x-auto">
                                                        {JSON.stringify(item.request.body, null, 2)}
                                                      </pre>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                          
                                          {/* Response Details */}
                                          {item.type === 'api' && item.response && (
                                            <div>
                                              <h5 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center">
                                                <Download className="w-4 h-4 mr-2 text-green-500" />
                                                Response Details
                                              </h5>
                                              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 text-sm">
                                                <div className="space-y-2">
                                                  <div>
                                                    <span className="font-medium text-gray-700 dark:text-gray-300">Status:</span>
                                                    <span className={`ml-2 ${
                                                      item.response.status >= 200 && item.response.status < 300 
                                                        ? 'text-green-600 dark:text-green-400' 
                                                        : 'text-red-600 dark:text-red-400'
                                                    }`}>
                                                      {item.response.status} {item.response.statusText}
                                                    </span>
                                                  </div>
                                                  <div>
                                                    <span className="font-medium text-gray-700 dark:text-gray-300">Response:</span>
                                                    <pre className="mt-1 text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 p-2 rounded overflow-x-auto max-h-40">
                                                      {JSON.stringify(item.response.data, null, 2)}
                                                    </pre>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                          
                                          {/* Selection Details */}
                                          {item.type === 'selection' && (
                                            <div className="lg:col-span-2">
                                              <h5 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center">
                                                <MousePointer className="w-4 h-4 mr-2 text-[#FE4215]" />
                                                Selection Details
                                              </h5>
                                              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 text-sm">
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                  <div>
                                                    <span className="font-medium text-gray-700 dark:text-gray-300">Action:</span>
                                                    <span className="ml-2 text-gray-600 dark:text-gray-400 capitalize">{item.action.replace('_', ' ')}</span>
                                                  </div>
                                                  {item.perkTitle && (
                                                    <div>
                                                      <span className="font-medium text-gray-700 dark:text-gray-300">Offer:</span>
                                                      <span className="ml-2 text-gray-600 dark:text-gray-400">{item.perkTitle}</span>
                                                    </div>
                                                  )}
                                                  {item.totalPerks && (
                                                    <div>
                                                      <span className="font-medium text-gray-700 dark:text-gray-300">Total Offers:</span>
                                                      <span className="ml-2 text-gray-600 dark:text-gray-400">{item.totalPerks}</span>
                                                    </div>
                                                  )}
                                                  <div>
                                                    <span className="font-medium text-gray-700 dark:text-gray-300">Status:</span>
                                                    <span className={`ml-2 ${
                                                      item.status === 'success' 
                                                        ? 'text-green-600 dark:text-green-400' 
                                                        : 'text-red-600 dark:text-red-400'
                                                    }`}>
                                                      {item.status}
                                                    </span>
                                                  </div>
                                                </div>
                                                {item.error && (
                                                  <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded text-red-700 dark:text-red-400">
                                                    <span className="font-medium">Error:</span> {item.error}
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </motion.div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 bg-gradient-to-r from-pink-100 to-rose-100 dark:from-pink-900/20 dark:to-rose-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Activity className="w-8 h-8 text-pink-600 dark:text-pink-400" />
                        </div>
                        <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">No journey recorded yet</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Start using the demo to see your API journey timeline
                        </p>
                      </div>
                    )
                  })()}
                </motion.div>
              )}
            </motion.div>

            {/* Wrap API Integration Section */}
            <motion.div
              variants={itemVariants}
              className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              {/* Section Header */}
              <div className="bg-gradient-to-r from-pink-50 to-rose-50 dark:from-pink-900/20 dark:to-rose-900/20 px-8 py-6 border-b border-pink-100 dark:border-pink-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-rose-600 rounded-xl flex items-center justify-center shadow-lg">
                      <Activity className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Wrap API Integration
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Session finalization and webhook payload generation
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 text-sm">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                    <span className="text-gray-600 dark:text-gray-400">Live API Monitor</span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-8">
                {(() => {
                  const wrapApiLog = apiLogs.find(log => 
                    log.action.includes('Wrap API') || log.action.includes('wrap')
                  )

                  if (wrapApiLog) {
                    return (
                      <div className="space-y-8">
                        {/* API Call Overview */}
                        <div className="bg-gradient-to-r from-pink-50 to-rose-50 dark:from-pink-900/20 dark:to-rose-900/20 rounded-xl p-6 border border-pink-200 dark:border-pink-800">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="text-lg font-semibold text-pink-900 dark:text-pink-100">
                              API Call Overview
                            </h4>
                            <div className="flex items-center space-x-2">
                              <span className={`w-3 h-3 rounded-full ${wrapApiLog.error ? 'bg-red-500' : 'bg-green-500'}`}></span>
                              <span className={`text-sm font-medium ${wrapApiLog.error ? 'text-red-700 dark:text-red-400' : 'text-green-700 dark:text-green-400'}`}>
                                {wrapApiLog.error ? 'Failed' : 'Success'}
                              </span>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
                              <div className="flex items-center space-x-2 mb-2">
                                <Clock className="w-4 h-4 text-blue-500" />
                                <span className="text-sm font-medium text-gray-900 dark:text-white">Request Time</span>
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {new Date(wrapApiLog.request.timestamp).toLocaleString()}
                              </p>
                            </div>
                            
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
                              <div className="flex items-center space-x-2 mb-2">
                                <Globe className="w-4 h-4 text-blue-500" />
                                <span className="text-sm font-medium text-gray-900 dark:text-white">Status Code</span>
                              </div>
                              <p className={`text-sm font-medium ${wrapApiLog.response?.status && wrapApiLog.response.status >= 200 && wrapApiLog.response.status < 300 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                {wrapApiLog.response?.status || 'N/A'}
                              </p>
                            </div>
                            
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
                              <div className="flex items-center space-x-2 mb-2">
                                <Activity className="w-4 h-4 text-blue-500" />
                                <span className="text-sm font-medium text-gray-900 dark:text-white">Response Time</span>
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {wrapApiLog.response?.timestamp ? new Date(wrapApiLog.response.timestamp).toLocaleString() : 'N/A'}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Request & Response Viewers */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {/* Request Viewer */}
                          <div className="bg-gradient-to-br from-slate-50 to-gray-50 dark:from-slate-900/50 dark:to-gray-900/50 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                            <div className="bg-slate-100 dark:bg-slate-800 px-4 py-3 border-b border-slate-200 dark:border-slate-700">
                              <div className="flex items-center space-x-2">
                                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                                <h5 className="font-semibold text-slate-900 dark:text-slate-100">API Request</h5>
                                <span className="text-xs text-slate-600 dark:text-slate-400 bg-slate-200 dark:bg-slate-700 px-2 py-1 rounded">
                                  {wrapApiLog.request.method || 'POST'}
                                </span>
                              </div>
                            </div>
                            
                            <div className="p-4">
                              <div className="mb-4">
                                <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2 block">
                                  Endpoint URL
                                </label>
                                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-slate-200 dark:border-slate-600">
                                  <code className="text-xs text-slate-900 dark:text-slate-100 break-all">
                                    {wrapApiLog.request.url}
                                  </code>
                                </div>
                              </div>
                              
                              <div className="mb-4">
                                <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2 block">
                                  Headers
                                </label>
                                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-slate-200 dark:border-slate-600">
                                  <pre className="text-xs text-slate-900 dark:text-slate-100 whitespace-pre-wrap">
{`Authorization: Bearer ${apiKey.substring(0, 20)}...
Content-Type: application/json`}
                                  </pre>
                                </div>
                              </div>
                              
                              <div>
                                <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2 block">
                                  Request Body
                                </label>
                                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-slate-200 dark:border-slate-600 max-h-48 overflow-y-auto">
                                  <pre className="text-xs text-slate-900 dark:text-slate-100 whitespace-pre-wrap">
                                    {JSON.stringify(wrapApiLog.request.body, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Response Viewer */}
                          <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl border border-green-200 dark:border-green-700 overflow-hidden">
                            <div className="bg-green-100 dark:bg-green-800/50 px-4 py-3 border-b border-green-200 dark:border-green-700">
                              <div className="flex items-center space-x-2">
                                <div className={`w-3 h-3 rounded-full ${wrapApiLog.error ? 'bg-red-500' : 'bg-green-500'}`}></div>
                                <h5 className="font-semibold text-green-900 dark:text-green-100">API Response</h5>
                                <span className={`text-xs px-2 py-1 rounded ${wrapApiLog.error ? 'bg-red-200 dark:bg-red-900/20 text-red-700 dark:text-red-400' : 'bg-green-200 dark:bg-green-900/20 text-green-700 dark:text-green-400'}`}>
                                  {wrapApiLog.response?.status || 'ERROR'}
                                </span>
                              </div>
                            </div>
                            
                            <div className="p-4">
                              {wrapApiLog.response ? (
                                <>
                                  <div className="mb-4">
                                    <label className="text-xs font-medium text-green-700 dark:text-green-300 mb-2 block">
                                      Response Headers
                                    </label>
                                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-green-200 dark:border-green-600">
                                      <pre className="text-xs text-green-900 dark:text-green-100 whitespace-pre-wrap">
{`Status: ${wrapApiLog.response.status} ${wrapApiLog.response.statusText || ''}
Content-Type: application/json`}
                                      </pre>
                                    </div>
                                  </div>
                                  
                                  <div>
                                    <label className="text-xs font-medium text-green-700 dark:text-green-300 mb-2 block">
                                      Response Body
                                    </label>
                                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-green-200 dark:border-green-600 max-h-48 overflow-y-auto">
                                      <pre className="text-xs text-green-900 dark:text-green-100 whitespace-pre-wrap">
                                        {JSON.stringify(wrapApiLog.response.data, null, 2)}
                                      </pre>
                                    </div>
                                  </div>
                                </>
                              ) : wrapApiLog.error ? (
                                <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 border border-red-200 dark:border-red-700">
                                  <div className="flex items-center space-x-2 mb-2">
                                    <XCircle className="w-5 h-5 text-red-500" />
                                    <span className="font-medium text-red-900 dark:text-red-100">Error Response</span>
                                  </div>
                                  <p className="text-sm text-red-800 dark:text-red-200">
                                    {wrapApiLog.error}
                                  </p>
                                </div>
                              ) : (
                                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 border border-yellow-200 dark:border-yellow-700">
                                  <div className="flex items-center space-x-2">
                                    <Clock className="w-5 h-5 text-yellow-500 animate-spin" />
                                    <span className="font-medium text-yellow-900 dark:text-yellow-100">Request in Progress</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* cURL Command */}
                        <div className="bg-gradient-to-r from-gray-50 to-slate-50 dark:from-gray-900/50 dark:to-slate-900/50 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                          <div className="bg-gray-100 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center space-x-2">
                              <Terminal className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                              <h5 className="font-semibold text-gray-900 dark:text-gray-100">cURL Command</h5>
                              <span className="text-xs text-gray-600 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded">
                                Copy & Paste Ready
                              </span>
                            </div>
                          </div>
                          
                          <div className="p-4">
                            <div className="bg-black dark:bg-gray-900 rounded-lg p-4 font-mono text-sm overflow-x-auto">
                              <pre className="text-green-400 whitespace-pre-wrap">
{`curl -X ${wrapApiLog.request.method || 'POST'} "${wrapApiLog.request.url}" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify(wrapApiLog.request.body, null, 2)}'`}
                              </pre>
                            </div>
                          </div>
                        </div>

                        {/* Webhook Payload (if successful) */}
                        {wrapApiLog.response?.data && !wrapApiLog.error && (
                          <div className="bg-gradient-to-r from-pink-50 to-rose-50 dark:from-pink-900/20 dark:to-rose-900/20 rounded-xl border border-pink-200 dark:border-pink-700 overflow-hidden">
                            <div className="bg-pink-100 dark:bg-pink-800/50 px-4 py-3 border-b border-pink-200 dark:border-pink-700">
                              <div className="flex items-center space-x-2">
                                <Webhook className="w-4 h-4 text-pink-600 dark:text-pink-400" />
                                <h5 className="font-semibold text-pink-900 dark:text-pink-100">Expected Webhook Payload</h5>
                                <span className="text-xs text-pink-700 dark:text-pink-400 bg-pink-200 dark:bg-pink-900/20 px-2 py-1 rounded">
                                  JSON Structure
                                </span>
                              </div>
                            </div>
                            
                            <div className="p-4">
                              <div className="mb-4">
                                <p className="text-sm text-pink-800 dark:text-pink-200 mb-3">
                                  This is the webhook payload structure that will be sent to your configured endpoint:
                                </p>
                                
                                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-pink-200 dark:border-pink-600 max-h-64 overflow-y-auto">
                                  <pre className="text-xs text-pink-900 dark:text-pink-100 whitespace-pre-wrap">
{JSON.stringify({
  body: {
    campaigns: [
      {
        campaign_id: 0,
        advertiser_name: "",
        title: "",
        short_headline: "",
        short_description: "",
        offer_description: "",
        terms_and_conditions: "",
        click_url: "",
        cta_yes: "",
        cta_no: "",
        mini_text: "",
        image: "",
        pixel: "",
        adv_pixel_url: "",
        save_for_later_url: "",
        description: "",
        is_loyaltyboost: false,
        loyaltyboost_requirements: {},
        offerwall_enabled: false,
        perkswallet_enabled: false,
        tags: [],
        beacons: {
          close: "",
          no_thanks_click: ""
        },
        creatives: [
          {
            id: 0,
            url: "",
            type: "",
            creative_type: "",
            aspect_ratio: 0,
            height: 0,
            width: 0,
            user_id: 0,
            is_primary: false
          }
        ],
        campaign: {
          is_product: false,
          offer_description: "",
          campaign_images: [
            {
              id: 0,
              url: "",
              type: "",
              creative_type: "",
              aspect_ratio: 0,
              height: 0,
              width: 0,
              user_id: 0,
              is_primary: false
            }
          ]
        },
        useraction_cta: null,
        useraction_url: null
      }
    ],
    pub_user_id: pubUserId,
    selected: 0,
    session_id: sessionId
  }
}, null, 2)}
                                  </pre>
                                </div>
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-purple-200 dark:border-purple-600">
                                  <div className="flex items-center space-x-2 mb-2">
                                    <User className="w-4 h-4 text-purple-500" />
                                    <span className="text-sm font-medium text-purple-900 dark:text-purple-100">User Context</span>
                                  </div>
                                  <p className="text-xs text-purple-700 dark:text-purple-300">
                                    <strong>pub_user_id:</strong> {pubUserId}
                                  </p>
                                  <p className="text-xs text-purple-700 dark:text-purple-300">
                                    <strong>session_id:</strong> {sessionId}
                                  </p>
                                </div>
                                
                                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-purple-200 dark:border-purple-600">
                                  <div className="flex items-center space-x-2 mb-2">
                                    <Target className="w-4 h-4 text-purple-500" />
                                    <span className="text-sm font-medium text-purple-900 dark:text-purple-100">Selection Status</span>
                                  </div>
                                  <p className="text-xs text-purple-700 dark:text-purple-300">
                                    <strong>selected:</strong> Indicates user selections (1 = yes, 0 = no)
                                  </p>
                                  <p className="text-xs text-purple-700 dark:text-purple-300">
                                    <strong>campaigns:</strong> Array of selected offers
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  } else {
                    return (
                      <div className="space-y-6">
                        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-6 border border-amber-200 dark:border-amber-700">
                          <div className="flex items-center space-x-3 mb-4">
                            <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                            <h5 className="font-semibold text-amber-900 dark:text-amber-100">
                              No Wrap API Call Detected
                            </h5>
                          </div>
                          <p className="text-sm text-amber-800 dark:text-amber-200 mb-4">
                            The Wrap API call was not found in the session logs. This typically occurs when:
                          </p>
                          <ul className="text-sm text-amber-700 dark:text-amber-300 space-y-2">
                            <li className="flex items-start space-x-2">
                              <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                              <span>The "Complete Purchase" button was not clicked in the payment flow</span>
                            </li>
                            <li className="flex items-start space-x-2">
                              <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                              <span>The API call failed to execute or was not properly logged</span>
                            </li>
                            <li className="flex items-start space-x-2">
                              <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                              <span>You navigated directly to this page without completing the demo flow</span>
                            </li>
                          </ul>
                        </div>

                        {/* Expected API Structure */}
                        <div className="bg-gradient-to-r from-gray-50 to-slate-50 dark:from-gray-900/50 dark:to-slate-900/50 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                          <div className="bg-gray-100 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center space-x-2">
                              <Code className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                              <h5 className="font-semibold text-gray-900 dark:text-gray-100">Expected API Structure</h5>
                            </div>
                          </div>
                          
                          <div className="p-4">
                            <div className="bg-black dark:bg-gray-900 rounded-lg p-4 font-mono text-sm overflow-x-auto">
                              <pre className="text-green-400 whitespace-pre-wrap">
{`curl -X POST "https://api.adspostx.com/sdk/v4/usp/session/${sessionId}.json" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "pub_user_id": "${pubUserId}"
  }'`}
                              </pre>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  }
                })()}
              </div>
            </motion.div>

            {/* Action Buttons */}
            <motion.div
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-4 justify-center pt-8"
            >
              <button
                onClick={() => navigate('/')}
                className="flex items-center justify-center space-x-2 px-8 py-4 bg-gradient-to-r from-pink-600 to-rose-700 hover:from-pink-700 hover:to-rose-800 text-white rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <Home className="w-5 h-5" />
                <span>Go to Home</span>
              </button>
              
              <button
                onClick={() => navigate('/usp-setup')}
                className="flex items-center justify-center space-x-2 px-8 py-4 bg-gradient-to-r from-[#FE4215] to-[#FF6B47] hover:from-[#E5380F] hover:to-[#E5380F] text-white rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <Package className="w-5 h-5" />
                <span>USP Setup</span>
              </button>
            </motion.div>
          </motion.div>
        </div>
      </div>
      {/* API Monitor Sidebar */}
      <ApiMonitor
        isVisible={showApiPanel}
        onClose={() => setShowApiPanel(false)}
        width={sidebarWidth}
        onWidthChange={setSidebarWidth}
        sessionId={sessionId}
        pubUserId={pubUserId}
        apiKey={apiKey}
        onGetSessionDetails={getSessionDetails}
        sessionDetailsLog={sessionDetailsLog}
        uspAllOffersChecked={null}
        hiddenTabs={['all-logs']}
      />

      {/* Theme Toggle Button */}
      <ThemeToggle />
    </div>
  )
}

export default FinalOrderConfirmationPage
